import * as React from "react"
import { CheckCircle2, LogIn, RefreshCw, Unplug } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { useUser } from "@/context/UserContext"
import { useBookingT } from "@/hooks/use-translations"
import type { Connection } from "@/features/integrations/api/types"
import { DisconnectModal } from "./DisconnectModal"

type Props = {
  integrationName: string
  connection: Connection | null
  onConnect: () => void
  onReconnect: () => void
  onDisconnect: () => void
  onTest: () => void
}

const LOGIN_PATH = "/site/login"

export function ActionsFooter({
  integrationName,
  connection,
  onConnect,
  onReconnect,
  onDisconnect,
  onTest,
}: Props) {
  const t = useBookingT()
  const { user } = useUser()
  const [disconnectOpen, setDisconnectOpen] = React.useState(false)
  const isErrored = connection?.health === "error" || connection?.health === "warning"

  const handleTest = () => {
    onTest()
    toast.success(t("integrations_test_success", "Test connection succeeded"))
  }

  // Guest: replace all actions with a single "Sign in to connect" CTA.
  if (!user) {
    const next = encodeURIComponent(window.location.pathname + window.location.search)
    return (
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-200 pt-4">
        <Button asChild>
          <a href={`${LOGIN_PATH}?next=${next}`}>
            <LogIn className="size-4" />
            {t("integrations_action_signin_to_connect", "Sign in to connect")}
          </a>
        </Button>
      </div>
    )
  }

  if (!connection) {
    // Connect button hidden until real Nango Connect flow is wired up.
    // {/*
    // <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-200 pt-4">
    //   <Button onClick={onConnect}>
    //     <Plug className="size-4" />
    //     {t("integrations_action_connect", "Connect")}
    //   </Button>
    // </div>
    // */}
    return null
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 pt-4">
        <Button variant="outline" onClick={handleTest}>
          <CheckCircle2 className="size-4" />
          {t("integrations_action_test", "Test connection")}
        </Button>
        <div className="flex flex-wrap gap-2">
          {isErrored && (
            <Button variant="outline" onClick={onReconnect}>
              <RefreshCw className="size-4" />
              {t("integrations_action_reconnect", "Reconnect")}
            </Button>
          )}
          <Button variant="destructive" onClick={() => setDisconnectOpen(true)}>
            <Unplug className="size-4" />
            {t("integrations_action_disconnect", "Disconnect")}
          </Button>
        </div>
      </div>

      <DisconnectModal
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        integrationName={integrationName}
        onConfirm={onDisconnect}
      />
    </>
  )
}
